/* @moldura padrao */
/* @moldura padrao */
/**
 * detalhe.tsx (viagens) — Tela de CONSULTA de uma viagem (read-only).
 *
 * Separada do formulário de edição (`form.tsx`): aqui se olha a viagem — quanto
 * gastou, em quê, quem lançou, e o que ficou pendente de ressarcimento. Editar
 * é em `/viagens/:id/editar`.
 *
 * O gasto por OS é o DIRETO (despesa lançada apontando aquela OS). As despesas
 * sem OS aparecem como "a ratear" — distribuí-las entre as OS pelo peso é a
 * Fatia 4 do plano; aqui não se inventa número.
 *
 * DUAS PERMISSÕES, DUAS METADES. `viagens.viagem.ver` abre a viagem (roteiro,
 * período, equipe, OS); `viagens.resumo.ver` abre o DINHEIRO (os quatro
 * números, categorias, despesas). O Gestor Administrativo tem a primeira e não
 * a segunda — é a fronteira com o financeiro, decidida na migration 066.
 *
 * Até 28/08/2026 a tela pedia as duas coisas num `Promise.all` e, sem a
 * segunda, o 403 derrubava a primeira junto: a Suany criava a viagem e caía em
 * "Viagem não encontrada". O resumo agora só é pedido por quem pode pedi-lo, e
 * a viagem aparece sozinha quando ele não vem.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/auth/auth-context';
import { formatarData, formatarDataHora } from '@/lib/data';
import { formatarDinheiro } from '@/lib/numero';
import type { ViagemDetalhe, ViagemResumo } from './shared';
import {
  agregaOS,
  badge,
  rotuloStatusViagem,
  rotuloTipoViagem,
  rotuloTransporte,
  varianteStatusViagem,
} from './shared';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

const ROTULO_FORMA: Record<string, string> = {
  banco_inter: 'Banco Inter',
  caixa: 'Caixa',
  dinheiro_proprio: 'Dinheiro próprio',
  cartao_proprio: 'Cartão próprio',
};

function StatCard(props: {
  label: string;
  valor: string;
  icone: string;
  cor: string;
  nota?: string;
}) {
  return (
    <div className="col-6 col-xl-3">
      <div className="card adminuiux-card mb-3 mb-lg-4">
        <div className="card-body p-4">
          <div className="row gx-3 align-items-center">
            <div className="col-auto">
              <span
                className={`avatar avatar-40 rounded-circle bg-opacity-10 ${props.cor}`}
              >
                <i className={`bi ${props.icone}`}></i>
              </span>
            </div>
            <div className="col">
              <p className="text-secondary small mb-1">{props.label}</p>
              <h5 className="mb-0">{props.valor}</h5>
              {props.nota && (
                <p className="text-secondary mb-0" style={{ fontSize: '0.72rem' }}>
                  {props.nota}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Dias de viagem contando as duas pontas ("19/06 a 22/06" = 4 dias) — a mesma
 * conta do `_dias` em `servico_viagens.py`. Existe aqui porque o número vem no
 * resumo, e o resumo é do financeiro: quem só pode ver a viagem continua
 * merecendo saber quantos dias ela durou.
 */
function diasEntre(de?: string | null, ate?: string | null): number | null {
  if (!de || !ate) return null;
  const a = new Date(`${de}T00:00:00`);
  const b = new Date(`${ate}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const dias = Math.round((b.getTime() - a.getTime()) / 86_400_000) + 1;
  return dias > 0 ? dias : null;
}

export function ViagemDetalhePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { pode } = useAuth();
  const abreDespesa = pode('despesas.lancamento.ver');
  const podeCusto = pode('viagens.resumo.ver');

  const [v, setV] = useState<ViagemDetalhe | null>(null);
  const [r, setR] = useState<ViagemResumo | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [confirmar, setConfirmar] = useState<'encerrar' | 'reabrir' | null>(null);
  const [processando, setProcessando] = useState(false);
  // Cancelar pede o porquê (03/11): a viagem que não aconteceu fica
  // registrada, na aba Canceladas, com o motivo.
  const podeEditar = pode('viagens.viagem.editar');
  const [cancelando, setCancelando] = useState(false);
  const [justificativa, setJustificativa] = useState('');

  useEffect(() => {
    let vivo = true;
    async function carregar() {
      setCarregando(true);
      try {
        const viagem = await api.get<ViagemDetalhe>(`/viagens/${id}`);
        if (!vivo) return;
        setV(viagem);
        // O resumo é a OUTRA permissão, e é acessório: se ele falhar, a viagem
        // continua na tela. Por isso vem depois e com `catch` próprio — juntar
        // os dois num `Promise.all` foi exatamente o que quebrou esta tela.
        if (podeCusto) {
          const resumo = await api
            .get<ViagemResumo>(`/viagens/${id}/resumo`)
            .catch(() => null);
          if (!vivo) return;
          setR(resumo);
        }
      } catch (err) {
        if (!vivo) return;
        toast.error(
          err instanceof ApiError ? err.message : 'Erro ao carregar a viagem.',
        );
      } finally {
        if (vivo) setCarregando(false);
      }
    }
    carregar();
    return () => {
      vivo = false;
    };
  }, [id, podeCusto]);

  async function acao(qual: 'encerrar' | 'reabrir') {
    setProcessando(true);
    try {
      const atualizada = await api.post<ViagemDetalhe>(`/viagens/${id}/${qual}`, {});
      setV(atualizada);
      toast.success(qual === 'encerrar' ? 'Viagem encerrada.' : 'Viagem reaberta.');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Não foi possível concluir a ação.',
      );
    } finally {
      setProcessando(false);
      setConfirmar(null);
    }
  }

  async function cancelarViagem() {
    setProcessando(true);
    try {
      const atualizada = await api.post<ViagemDetalhe>(`/viagens/${id}/cancelar`, {
        justificativa,
      });
      setV(atualizada);
      toast.success('Viagem cancelada.');
      setCancelando(false);
      setJustificativa('');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Não foi possível cancelar a viagem.',
      );
    } finally {
      setProcessando(false);
    }
  }

  async function reativarViagem() {
    setProcessando(true);
    try {
      setV(await api.post<ViagemDetalhe>(`/viagens/${id}/reativar`, {}));
      toast.success('Viagem reativada.');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Não foi possível reativar a viagem.',
      );
    } finally {
      setProcessando(false);
    }
  }

  if (carregando) {
    return (
      <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
        <span className="spinner-border spinner-border-sm" /> Carregando…
      </div>
    );
  }
  // Só a VIAGEM decide se a tela existe. O resumo é a outra metade, e a falta
  // dele significa "sem acesso ao dinheiro", nunca "viagem não encontrada".
  if (!v) {
    return <div className="container py-5 text-center text-danger">Viagem não encontrada.</div>;
  }
  const custo = podeCusto ? r : null;
  // Quantos dias a viagem durou não é dinheiro: sem o resumo, conta-se das
  // datas da própria viagem (mesma conta do `_dias` do serviço).
  const dias = custo?.dias ?? diasEntre(v.data_partida, v.data_chegada);

  const roteiro = [v.origem, ...(v.destinos ?? [])].filter(Boolean).join(' → ');
  const encerrada = Boolean(v.encerrada_em);
  const comOS = agregaOS(v.tipo);

  const periodo =
    v.data_partida && v.data_chegada
      ? `${formatarData(v.data_partida)} – ${formatarData(v.data_chegada)}`
      : v.data_partida
        ? formatarData(v.data_partida)
        : '—';

  const ficha: [string, string][] = [
    ['Tipo', rotuloTipoViagem(v.tipo)],
    ['Loja (empresa)', v.loja_nome ?? '—'],
    ['Transporte', rotuloTransporte(v.transporte)],
    ['Período', periodo],
    ['Dias', dias ? `${dias} dia${dias === 1 ? '' : 's'}` : '—'],
    ['Participantes', (v.instaladores ?? []).map((i) => i.nome).join(', ') || '—'],
    ['Criada em', v.criado_em ? formatarDataHora(v.criado_em) : '—'],
  ];
  if (encerrada) {
    ficha.push([
      'Encerrada em',
      `${formatarDataHora(v.encerrada_em!)}${v.encerrada_por ? ` por ${v.encerrada_por}` : ''}`,
    ]);
  }

  return (
    <CustomMolduraPagina
      titulo={roteiro || `Viagem ${v.id}`}
      descricao={
        `${rotuloTransporte(v.transporte)} · ${periodo}` +
        (v.loja_nome ? ` · ${v.loja_nome}` : '')
      }
      caminho={[{ rotulo: 'Viagens', para: '/viagens' }]}
      acoes={
        <>
          {/* O SELO DE ESTADO vem PRIMEIRO nas ações. Ele não é ação — é
              informação —, mas fica na mesma zona porque o topo tem uma linha
              só para o título, e um selo dentro do `<h5>` faz o título mudar de
              tamanho conforme a palavra do estado. */}
          <span className={`badge ${badge(varianteStatusViagem(v.status))}`}>
            {rotuloStatusViagem(v.status)}
          </span>
          <button
            className="btn btn-outline-theme"
            onClick={() => navigate(`/viagens/${v.id}/editar`)}
          >
            <i className="bi bi-pencil me-1"></i> Editar
          </button>
          {podeEditar && !encerrada && v.status !== 'cancelada' && (
            <button
              className="btn btn-outline-theme text-danger"
              onClick={() => setCancelando(true)}
            >
              <i className="bi bi-x-circle me-1"></i> Cancelar viagem
            </button>
          )}
          {podeEditar && v.status === 'cancelada' && (
            <button
              className="btn btn-outline-theme"
              onClick={reativarViagem}
              disabled={processando}
            >
              <i className="bi bi-arrow-counterclockwise me-1"></i> Reativar
            </button>
          )}
          {encerrada ? (
            <button className="btn btn-outline-theme" onClick={() => setConfirmar('reabrir')}>
              <i className="bi bi-unlock me-1"></i> Reabrir
            </button>
          ) : v.status === 'cancelada' ? null : (
            <button
              className="btn btn-theme"
              onClick={() => setConfirmar('encerrar')}
            >
              <i className="bi bi-check2-circle me-1"></i> Encerrar viagem
            </button>
          )}
        </>
      }
    >

      {encerrada && (
        <div className="alert alert-success d-flex align-items-center gap-2 py-2">
          <i className="bi bi-lock"></i>
          <span className="small">
            Viagem encerrada em {formatarDataHora(v.encerrada_em!)}
            {v.encerrada_por ? ` por ${v.encerrada_por}` : ''}. Despesa que chegar
            depois disso exige reabrir a viagem.
          </span>
        </div>
      )}

      {v.status === 'cancelada' && (
        <div className="alert alert-secondary d-flex align-items-start gap-2 py-2">
          <i className="bi bi-x-circle mt-1"></i>
          <div className="small">
            <div>
              Viagem cancelada
              {v.cancelada_em ? ` em ${formatarDataHora(v.cancelada_em)}` : ''}
              {v.cancelada_por ? ` por ${v.cancelada_por}` : ''}.
            </div>
            {v.justificativa_cancelamento ? (
              <div className="mt-1" style={{ whiteSpace: 'pre-line' }}>
                <strong>Motivo:</strong> {v.justificativa_cancelamento}
              </div>
            ) : (
              <div className="mt-1 text-secondary">Sem motivo registrado.</div>
            )}
          </div>
        </div>
      )}

      {/* Números — a metade do dinheiro, só para `viagens.resumo.ver`. */}
      {custo && (
        <div className="row">
          <StatCard
            label="Total gasto"
            valor={formatarDinheiro(custo.total_gasto)}
            icone="bi-cash-stack"
            cor="bg-danger text-danger"
            nota={`${custo.qtd_despesas} despesa${custo.qtd_despesas === 1 ? '' : 's'}`}
          />
          <StatCard
            label="Custo por dia"
            valor={custo.custo_dia != null ? formatarDinheiro(custo.custo_dia) : '—'}
            icone="bi-calendar-range"
            cor="bg-info text-info"
            nota={dias ? `${dias} dia${dias === 1 ? '' : 's'} de viagem` : 'sem período'}
          />
          <StatCard
            label="A ressarcir"
            valor={formatarDinheiro(custo.pendente_ressarcimento)}
            icone="bi-arrow-return-left"
            cor="bg-warning text-warning"
            nota="pago do próprio bolso"
          />
          <StatCard
            label={comOS ? 'A ratear entre as OS' : 'Sem OS'}
            valor={comOS ? formatarDinheiro(custo.nao_atribuido) : '—'}
            icone="bi-diagram-3"
            cor="bg-secondary text-secondary"
            nota={comOS ? 'despesas sem OS específica' : 'viagem não operacional'}
          />
        </div>
      )}

      <div className="row g-3 g-lg-4 mb-3 mb-lg-4">
        {/* Ficha — ocupa a largura toda quando não há o cartão de categorias
            ao lado (quem não vê custo não fica com meia tela vazia). */}
        <div className={custo ? 'col-12 col-lg-4' : 'col-12'}>
          <div className="card adminuiux-card h-100">
            <div className="card-header bg-transparent">
              <h6 className="mb-0">
                <i className="bi bi-info-circle text-theme-1 me-2"></i>A viagem
              </h6>
            </div>
            <div className="card-body p-4">
              {ficha.map(([rotulo, valor]) => (
                <div key={rotulo} className="row gx-3 mb-2">
                  <div className="col-5 text-secondary small">{rotulo}</div>
                  <div className="col-7 small">{valor}</div>
                </div>
              ))}
              {v.observacoes && (
                <>
                  <hr className="my-2" />
                  <p className="text-secondary small mb-1">Observações</p>
                  <p className="small mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    {v.observacoes}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Gasto por categoria */}
        {custo && (
        <div className="col-12 col-lg-8">
          <div className="card adminuiux-card h-100">
            <div className="card-header bg-transparent">
              <h6 className="mb-0">
                <i className="bi bi-tags text-theme-1 me-2"></i>Gasto por categoria
              </h6>
            </div>
            <div className="card-body p-4">
              {custo.por_categoria.length === 0 ? (
                <p className="text-secondary small mb-0 py-3 text-center">
                  Nenhuma despesa lançada nesta viagem.
                </p>
              ) : (
                custo.por_categoria.map((c) => {
                  const pct =
                    custo.total_gasto > 0 ? (c.total / custo.total_gasto) * 100 : 0;
                  return (
                    <div key={c.categoria} className="mb-3">
                      <div className="d-flex justify-content-between small mb-1">
                        <span>{c.categoria}</span>
                        <span className="text-secondary">
                          {formatarDinheiro(c.total)}{' '}
                          <span style={{ fontSize: '0.72rem' }}>({pct.toFixed(0)}%)</span>
                        </span>
                      </div>
                      <div className="progress" style={{ height: 6 }}>
                        <div
                          className="progress-bar bg-theme-1"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
        )}
      </div>

      {/* OS da viagem — só na operacional */}
      {comOS && (
        <div className="card adminuiux-card mb-3 mb-lg-4">
          <div className="card-header bg-transparent">
            <h6 className="mb-0">
              <i className="bi bi-clipboard-check text-theme-1 me-2"></i>
              OS da viagem
              {v.os.length > 0 && (
                <span className="badge text-bg-secondary ms-2">{v.os.length}</span>
              )}
            </h6>
          </div>
          <div className="table-responsive px-3 px-lg-4 pb-3">
            <table className="table align-middle mb-0" style={{ fontSize: '0.9rem' }}>
              <thead>
                <tr className="text-secondary text-uppercase" style={{ fontSize: '0.7rem' }}>
                  <th>OS</th>
                  <th>Cliente</th>
                  {/* Peso e gasto direto são o rateio — a metade do dinheiro. */}
                  {custo && <th className="text-center">Peso</th>}
                  {custo && <th className="text-end">Gasto direto</th>}
                </tr>
              </thead>
              <tbody>
                {/* Sem o resumo, as OS vêm da própria viagem: quais são é
                    roteiro, quanto custaram é financeiro. */}
                {(custo ? custo.por_os : v.os).map((o) => (
                  <tr
                    key={o.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/ordens-servico/${o.id}`)}
                  >
                    <td className="fw-semibold">{o.codigo}</td>
                    <td className="text-secondary">{o.cliente_nome ?? '—'}</td>
                    {custo && (
                      <td className="text-center text-secondary">
                        {'peso' in o ? o.peso : '—'}
                      </td>
                    )}
                    {custo && (
                      <td className="text-end">
                        {'total_direto' in o ? formatarDinheiro(o.total_direto) : '—'}
                      </td>
                    )}
                  </tr>
                ))}
                {(custo ? custo.por_os : v.os).length === 0 && (
                  <tr>
                    <td colSpan={custo ? 4 : 2} className="py-4 text-center text-secondary">
                      Nenhuma OS incluída nesta viagem.
                    </td>
                  </tr>
                )}
              </tbody>
              {custo && custo.nao_atribuido > 0 && (
                <tfoot>
                  <tr className="text-secondary">
                    <td colSpan={3} className="small">
                      <i className="bi bi-info-circle me-1"></i>
                      Despesas sem OS específica (a ratear entre as OS acima)
                    </td>
                    <td className="text-end fw-semibold">
                      {formatarDinheiro(custo.nao_atribuido)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Despesas — a lista inteira do gasto da viagem, com valor, quem lançou
          e comprovante. É a metade do dinheiro; sai da tela sem a permissão. */}
      {custo && (
      <div className="card adminuiux-card mb-3 mb-lg-4">
        <div className="card-header bg-transparent d-flex align-items-center justify-content-between">
          <h6 className="mb-0">
            <i className="bi bi-receipt text-theme-1 me-2"></i>
            Despesas
            {custo.qtd_despesas > 0 && (
              <span className="badge text-bg-secondary ms-2">{custo.qtd_despesas}</span>
            )}
          </h6>
          {/* Formulário COMPLETO: a tela da viagem é de gestão, no desktop, e
              quem lança daqui está conferindo o gasto da viagem inteira. */}
          <button
            className="btn btn-sm btn-outline-theme"
            onClick={() => navigate('/despesas/lancar')}
          >
            <i className="bi bi-plus-lg me-1"></i> Lançar despesa
          </button>
        </div>
        <div className="table-responsive px-3 px-lg-4 pb-3">
          <table className="table align-middle table-hover mb-0" style={{ fontSize: '0.9rem' }}>
            <thead>
              <tr className="text-secondary text-uppercase" style={{ fontSize: '0.7rem' }}>
                <th>Data</th>
                <th>Descrição</th>
                <th>Categoria</th>
                <th>OS</th>
                <th>Quem lançou</th>
                <th>Pagamento</th>
                <th className="text-end">Valor</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {custo.despesas.map((d) => (
                // A linha só abre a despesa para quem pode ler despesa: a sócia vê o
                // resumo da viagem (`viagens.resumo.ver`) e caía num formulário vazio
                // com 403 (varredura de 16/09).
                <tr
                  key={d.id}
                  style={abreDespesa ? { cursor: 'pointer' } : undefined}
                  onClick={abreDespesa ? () => navigate(`/despesas/${d.id}`) : undefined}
                >
                  <td className="text-nowrap text-secondary">
                    {d.data ? formatarData(d.data) : '—'}
                  </td>
                  <td>{d.descricao || '—'}</td>
                  <td className="text-secondary">{d.categoria ?? '—'}</td>
                  <td className="text-secondary">{d.os_codigo ?? '—'}</td>
                  <td className="text-secondary">{d.lancado_por ?? '—'}</td>
                  <td className="text-nowrap">
                    <span className="badge text-bg-light">
                      {d.forma_pgto ? (ROTULO_FORMA[d.forma_pgto] ?? d.forma_pgto) : '—'}
                    </span>
                    {d.restituicao === 'pendente' && (
                      <span className="badge text-bg-warning ms-1">a ressarcir</span>
                    )}
                    {d.restituicao === 'restituido' && (
                      <span className="badge text-bg-success ms-1">ressarcido</span>
                    )}
                  </td>
                  <td className="text-end fw-semibold">{formatarDinheiro(d.valor)}</td>
                  <td className="text-end" onClick={(e) => e.stopPropagation()}>
                    {d.comprovante && (
                      <a
                        href={`/uploads/${d.comprovante.replace(/^\/uploads\//, '')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-link btn-square"
                        title="Ver comprovante"
                      >
                        <i className="bi bi-paperclip"></i>
                      </a>
                    )}
                  </td>
                </tr>
              ))}
              {custo.despesas.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-5 text-center text-secondary">
                    Nenhuma despesa lançada nesta viagem.
                  </td>
                </tr>
              )}
            </tbody>
            {custo.despesas.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={6} className="text-end text-secondary small">
                    Total
                  </td>
                  <td className="text-end fw-bold">{formatarDinheiro(custo.total_gasto)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      )}

      {/* Cancelar — o motivo é obrigatório */}
      <Modal
        className="casaa-modal"
        show={cancelando}
        onHide={() => setCancelando(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancelar viagem</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-secondary">
            A viagem não some: vai para a aba Canceladas, com o motivo. Dá para
            reativar depois.
          </p>
          <div className="form-floating">
            <textarea
              id="campo-justificativa-cancelamento"
              className="form-control"
              style={{ height: '120px' }}
              placeholder="Por que a viagem não aconteceu?"
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              autoFocus
            />
            <label htmlFor="campo-justificativa-cancelamento">
              Por que a viagem não aconteceu?
            </label>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-link" onClick={() => setCancelando(false)}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={cancelarViagem}
            disabled={processando || !justificativa.trim()}
          >
            {processando && <span className="spinner-border spinner-border-sm me-1" />}
            Cancelar viagem
          </button>
        </Modal.Footer>
      </Modal>

      {/* Confirmar encerrar / reabrir */}
      <Modal className="casaa-modal" show={confirmar !== null} onHide={() => setConfirmar(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {confirmar === 'encerrar' ? 'Encerrar viagem' : 'Reabrir viagem'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmar === 'encerrar' ? (
            <>
              Encerrar fecha as contas desta viagem
              {custo ? (
                <>
                  : o total de{' '}
                  <strong>{formatarDinheiro(custo.total_gasto)}</strong> passa a ser o
                  custo definitivo
                </>
              ) : (
                ': o gasto lançado até aqui passa a ser o custo definitivo'
              )}
              . Uma despesa que chegar depois vai exigir reabrir a viagem.
            </>
          ) : (
            <>
              Reabrir permite lançar novas despesas nesta viagem. O total pode mudar, e
              isso afeta o custo das OS envolvidas.
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-link" onClick={() => setConfirmar(null)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-theme"
            onClick={() => confirmar && acao(confirmar)}
            disabled={processando}
          >
            {processando && <span className="spinner-border spinner-border-sm me-1" />}
            {confirmar === 'encerrar' ? 'Encerrar' : 'Reabrir'}
          </button>
        </Modal.Footer>
      </Modal>
    </CustomMolduraPagina>
  );
}
